<!-- CouponUserCardModal.vue -->
<script setup>
import { ref, computed, watch } from "vue";
import { useCouponUserStore } from "@/stores/useCouponUserStore";
import { formatDateTimeForDisplay } from "@/utils/dateUtils";
import api from "@/services/api";
import { useDraggableModal } from "@/composables/useDraggableModal";

const props = defineProps({
  show:          { type: Boolean, required: true },
  couponUserId:  { type: Number,  required: true },
});

const emit = defineEmits(["close"]);

const store         = useCouponUserStore();
const isLoadingData = ref(true);

// 🔹 NOVO: arrastar o modal pelo cabeçalho. Nem todo uso desmonta o componente
// entre aberturas, então o watch de `show` recentraliza ao reabrir.
const {
  panelRef,
  panelStyle,
  isDragging,
  isDraggable,
  onHandlePointerDown,
  resetPosition,
} = useDraggableModal();

watch(
  () => props.show,
  (val) => {
    if (val) resetPosition();
  }
);
const cu            = ref(null);

// ─────────────────────────────────────────────
// FETCH — busca direto pelo ID para ter todos os campos
// (mesmo padrão de SubscriptionCardModal)
// ─────────────────────────────────────────────
watch(
  () => props.couponUserId,
  async (newId) => {
    if (!newId) return;
    isLoadingData.value = true;
    cu.value = null;
    try {
      const cached = store.couponUsers.find((x) => x.couponUserId === newId);
      if (cached) {
        cu.value = cached;
      } else {
        const response = await api.get(`/CouponUsers/${newId}`);
        cu.value = response.data;
      }
    } catch (e) {
      console.error("❌ Erro ao carregar coupon_user no CardModal:", e);
    } finally {
      isLoadingData.value = false;
    }
  },
  { immediate: true }
);

// ─────────────────────────────────────────────
// HELPERS DE STATUS
// ─────────────────────────────────────────────

// Chip do status de revogação
const revokedChip = computed(() => {
  if (!cu.value) return { label: "—", cls: "badge-gray", icon: "fas fa-question-circle" };
  return cu.value.revokedAt
    ? { label: "Revogado", cls: "badge-red",   icon: "fas fa-ban" }
    : { label: "Ativo",    cls: "badge-green", icon: "fas fa-check-circle" };
});

// Chip do status de resgate
const redeemedChip = computed(() => {
  if (!cu.value) return { label: "—", cls: "badge-gray", icon: "fas fa-question-circle" };
  return cu.value.redeemedAt
    ? { label: "Resgatado",  cls: "badge-green",  icon: "fas fa-gift" }
    : { label: "Não usado",  cls: "badge-yellow", icon: "fas fa-hourglass-half" };
});

function displayVal(v) {
  return v != null && v !== "" ? v : "—";
}
</script>

<template>
  <div v-if="show" class="modal-overlay" @click.self="emit('close')">
    <div ref="panelRef" class="card-modal" :style="panelStyle">

      <!-- HEADER -->
      <div
        class="modal-header"
        :class="{
 'modal-header--draggable': isDraggable,
          'modal-header--dragging': isDragging,
        }"
        @pointerdown="onHandlePointerDown"
      >
        <h3 class="card-title">
          Detalhes do Vínculo de Cupom
          <span v-if="cu" class="title-id">(ID: {{ cu.couponUserId }})</span>
        </h3>
        <button @click="emit('close')" class="modal-close-btn" aria-label="Fechar">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd"
              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
              clip-rule="evenodd"/>
          </svg>
        </button>
      </div>

      <!-- LOADING -->
      <div v-if="isLoadingData" class="loading-state">
        <i class="fas fa-spinner fa-spin"></i> Carregando detalhes...
      </div>

      <!-- CONTEÚDO -->
      <div v-else-if="cu" class="card-body">

        <!-- ── SEÇÃO: Identificação ───────────────────────────────── -->
        <section class="card-section">
          <h4 class="section-title">Identificação</h4>

          <div class="info-grid info-grid-3col">
            <div class="info-item">
              <span class="info-label">ID Vínculo</span>
              <span class="info-value mono">{{ cu.couponUserId }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Cupom</span>
              <span class="info-value mono">
                {{ cu.couponCode ?? cu.couponId }}
                <span v-if="cu.couponCode" class="id-muted">({{ cu.couponId }})</span>
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Usuário</span>
              <span class="info-value mono">
                {{ cu.userName ?? cu.userId }}
                <span v-if="cu.userName" class="id-muted">({{ cu.userId }})</span>
              </span>
            </div>
          </div>

          <!-- Contato do usuário -->
          <div class="info-grid info-grid-1col info-grid-spaced">
            <div class="info-item">
              <span class="info-label">E-mail do Usuário</span>
              <span class="info-value copyable-wrap">{{ displayVal(cu.userEmail) }}</span>
            </div>
          </div>
        </section>

        <hr class="divider"/>

        <!-- ── SEÇÃO: Status do Vínculo ──────────────────────────── -->
        <section class="card-section">
          <h4 class="section-title">Status do Vínculo</h4>

          <div class="info-grid info-grid-2col">
            <div class="info-item info-item-chip">
              <span class="info-label">Situação</span>
              <span :class="['badge', revokedChip.cls]">
                <i :class="revokedChip.icon"></i> {{ revokedChip.label }}
              </span>
            </div>
            <div class="info-item info-item-chip">
              <span class="info-label">Resgate</span>
              <span :class="['badge', redeemedChip.cls]">
                <i :class="redeemedChip.icon"></i> {{ redeemedChip.label }}
              </span>
            </div>
          </div>
        </section>

        <hr class="divider"/>

        <!-- ── SEÇÃO: Datas ──────────────────────────────────────── -->
        <section class="card-section">
          <h4 class="section-title">Datas</h4>
          <div class="info-grid info-grid-3col">
            <div class="info-item">
              <span class="info-label">Atribuído em</span>
              <span class="info-value">{{ formatDateTimeForDisplay(cu.assignedAt) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Revogado em</span>
              <span class="info-value">
                {{ cu.revokedAt ? formatDateTimeForDisplay(cu.revokedAt) : "—" }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Resgatado em</span>
              <span class="info-value">
                {{ cu.redeemedAt ? formatDateTimeForDisplay(cu.redeemedAt) : "—" }}
              </span>
            </div>
          </div>
        </section>

      </div>

      <!-- ERRO / vazio -->
      <div v-else class="loading-state">
        Vínculo não encontrado.
      </div>

    </div>
  </div>
</template>

<style scoped>
.card-modal {
  background: var(--surface);
  border-radius: 12px;
  width: 90%;
  max-width: 680px;
  box-shadow: var(--shadow-modal);
  margin-bottom: 50px;
  overflow: hidden;
  max-height: 88vh;
  display: flex;
  flex-direction: column;
}

.card-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-dark);
}

.title-id {
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-left: 4px;
}

.card-body {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0;
  overflow-y: auto;
}

.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 500;
  border-radius: 9999px;
  white-space: nowrap;
}

.badge-green  { background-color: #b2f2bb; color: #2b8a3e; }
.badge-red    { background-color: #ffc9c9; color: #c92a2a; }
.badge-yellow { background-color: var(--warning-bg); color: #854d0e; }
.badge-gray   { background-color: #f1f3f5; color: #495057; }

.card-section {
  padding: 0.25rem 0 0.75rem;
}

.section-title {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-subtle);
  margin: 0 0 0.6rem 0;
}

.divider {
  border: none;
  border-top: 1px dashed var(--border-color);
  margin: 0 0 0.75rem 0;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.5rem 1rem;
}

.info-grid-spaced {
  margin-top: 0.5rem;
}

.info-grid-1col { grid-template-columns: 1fr !important; }
.info-grid-2col { grid-template-columns: repeat(2, 1fr) !important; }
.info-grid-3col { grid-template-columns: repeat(3, 1fr) !important; }

.info-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.info-item-chip {
  align-items: flex-start;
}

.info-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
}

.info-value {
  font-size: 0.88rem;
  color: var(--text-dark);
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 0.82rem;
}

.id-muted {
  color: var(--text-subtle);
  font-size: 0.78rem;
  margin-left: 2px;
}

.copyable-wrap {
  white-space: normal;
  overflow: visible;
  word-break: break-all;
}

</style>